import React, { useState } from "react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Card } from "./ui/card";
import PasswordInput from "./PasswordInput";
import PasswordStrengthIndicator, { isPasswordStrong } from "./PasswordStrengthIndicator";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

export default function ChangePasswordCard() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!isPasswordStrong(next)) {
      toast.error("New password must be 8+ chars with uppercase, lowercase, and a special character.");
      return;
    }
    if (next !== confirm) { toast.error("New passwords don't match"); return; }
    if (next === current) { toast.error("New password must be different from current"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated successfully");
      setCurrent(""); setNext(""); setConfirm(""); setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to update password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6" data-testid="change-password-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <span className="h-9 w-9 rounded-md bg-blue-50 text-[#002FA7] grid place-items-center flex-shrink-0">
            <KeyRound className="h-4 w-4" />
          </span>
          <div>
            <h3 className="font-display font-bold tracking-tight text-zinc-950">Password</h3>
            <p className="text-xs text-zinc-500 mt-0.5">Change the password on this account. Requires your current password.</p>
          </div>
        </div>
        {!open && (
          <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="change-password-open-btn">
            Change password
          </Button>
        )}
      </div>
      {open && (
        <form onSubmit={submit} className="mt-5 space-y-3 max-w-md" data-testid="change-password-form">
          <div>
            <Label htmlFor="current_pw">Current password</Label>
            <PasswordInput
              id="current_pw"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="cp-current-input"
              showTestId="cp-current-toggle"
              className="mt-1.5"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="next_pw">New password</Label>
            <PasswordInput
              id="next_pw"
              required
              minLength={8}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="cp-new-input"
              showTestId="cp-new-toggle"
              className="mt-1.5"
            />
            <PasswordStrengthIndicator password={next} />
          </div>
          <div>
            <Label htmlFor="confirm_pw">Confirm new password</Label>
            <PasswordInput
              id="confirm_pw"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="cp-confirm-input"
              showTestId="cp-confirm-toggle"
              className="mt-1.5"
            />
            {confirm && next !== confirm && (
              <p className="text-xs text-red-600 mt-1">Passwords don&apos;t match yet.</p>
            )}
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#002FA7] hover:bg-[#00227A] text-white"
              data-testid="cp-submit-btn"
            >
              {saving ? "Updating…" : "Update password"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setOpen(false); setCurrent(""); setNext(""); setConfirm(""); }}
              data-testid="cp-cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
